import { ReactNode, useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth, useCanAccessReports } from '../contexts/AuthContext';
import type { MemberRoleType } from '../contexts/AuthContext';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  DocumentTextIcon,
  ShoppingCartIcon,
  UsersIcon,
  TruckIcon,
  ChartBarIcon,
  DocumentChartBarIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  ChevronDownIcon,
  CogIcon,
  ClockIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';

interface LayoutProps {
  children: ReactNode;
}

// Helper function to get role styling
const getRoleDisplayInfo = (roleType?: MemberRoleType) => {
  switch (roleType) {
    case 'SST':
      return {
        label: 'SST',
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-200',
        description: 'Strategic Sourcing Team'
      };
    case 'AM':
      return {
        label: 'AM',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        borderColor: 'border-blue-200',
        description: 'Account Manager'
      };
    case 'Supplier':
      return {
        label: 'Supplier',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-200',
        description: 'Supplier (Internal)'
      };
    case 'QC':
      return {
        label: 'QC',
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-800',
        borderColor: 'border-purple-200',
        description: 'Quality Control'
      };
    default:
      return {
        label: 'User',
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
        borderColor: 'border-gray-200',
        description: 'Standard User'
      };
  }
};

// Navigation configuration with icons and grouping
const getNavigationConfig = (user: any, location: ReturnType<typeof useLocation>, canAccessReports: boolean) => {
  const isAdmin = user?.roleType === 'SST' || user?.roleType === 'AM';

  // Core navigation items (always visible)
  const coreNavigation = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: HomeIcon,
      current: location.pathname === '/dashboard',
      tooltip: 'Dashboard Overview'
    },
    {
      name: 'Quotes',
      href: '/quotes',
      icon: DocumentTextIcon,
      current: location.pathname === '/quotes',
      tooltip: 'Quote Management'
    },
    {
      name: 'Orders',
      href: '/orders',
      icon: ShoppingCartIcon,
      current: location.pathname === '/orders',
      tooltip: 'Order Management'
    },
  ];

  // Add Reports navigation item if user has access
  if (canAccessReports && user?.userType === 'Member') {
    coreNavigation.push({
      name: 'Reports',
      href: '/reports',
      icon: DocumentChartBarIcon,
      current: location.pathname.startsWith('/reports'),
      tooltip: 'Business Reports & Analytics'
    });
  }

  // Admin navigation items (dropdown menu)
  const adminNavigation = isAdmin ? [
    {
      name: 'Members',
      href: '/members',
      icon: UsersIcon,
      current: location.pathname.startsWith('/members'),
      tooltip: 'Member Management'
    },
    {
      name: 'Suppliers',
      href: '/suppliers',
      icon: TruckIcon,
      current: location.pathname.startsWith('/suppliers'),
      tooltip: 'Supplier Management'
    },
    {
      name: 'Exchange Rates',
      href: '/admin/exchange-rates',
      icon: BanknotesIcon,
      current: location.pathname === '/admin/exchange-rates',
      tooltip: 'Manage Exchange Rates'
    },
    {
      name: 'Timeline Report',
      href: '/reports/timeline',
      icon: ClockIcon,
      current: location.pathname === '/reports/timeline',
      tooltip: 'Quote Timeline Analytics'
    },
    {
      name: 'Monitoring',
      href: '/monitoring',
      icon: ChartBarIcon,
      current: location.pathname === '/monitoring',
      tooltip: 'LLM Monitoring'
    },
  ] : [];

  return { coreNavigation, adminNavigation, isAdmin };
};

const ResponsiveLayout = ({ children }: LayoutProps) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const canAccessReports = useCanAccessReports();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isAdminDropdownOpen, setIsAdminDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const adminDropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileToggleRef = useRef<HTMLButtonElement>(null);

  // Get role display information
  const roleInfo = getRoleDisplayInfo(user?.roleType);
  const { coreNavigation, adminNavigation, isAdmin } = getNavigationConfig(user, location, canAccessReports);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (adminDropdownRef.current && !adminDropdownRef.current.contains(event.target as Node)) {
        setIsAdminDropdownOpen(false);
      }
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node) &&
        !mobileToggleRef.current?.contains(event.target as Node)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsAdminDropdownOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
    setIsAdminDropdownOpen(false);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo and Desktop Navigation */}
            <div className="flex items-center min-w-0 flex-1">
              <div className="flex-shrink-0 flex items-center mr-6">
                <h1 className="text-xl font-bold text-gray-900 whitespace-nowrap">
                  Mercury
                </h1>
              </div>
              
              {/* Desktop Icon Navigation */}
              <div className="hidden lg:flex lg:items-center lg:space-x-1">
                {/* Core Navigation Items */}
                {coreNavigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`${
                        item.current
                          ? 'bg-blue-50 border-blue-500 text-blue-700'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      } group relative inline-flex items-center px-3 py-2 border-b-2 text-sm font-medium transition-all duration-200 rounded-t-md`}
                      title={item.tooltip}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="ml-2 hidden xl:inline">{item.name}</span>
                      
                      {/* Tooltip for smaller screens */}
                      <div className="xl:hidden absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
                        {item.tooltip}
                      </div>
                    </Link>
                  );
                })}


                {/* Admin Dropdown */}
                {isAdmin && (
                  <div className="relative" ref={adminDropdownRef}>
                    <button
                      onClick={() => setIsAdminDropdownOpen(!isAdminDropdownOpen)}
                      className={`${
                        adminNavigation.some(item => item.current)
                          ? 'bg-blue-50 border-blue-500 text-blue-700'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                      } group relative inline-flex items-center px-3 py-2 border-b-2 text-sm font-medium transition-all duration-200 rounded-t-md`}
                    >
                      <CogIcon className="h-5 w-5" />
                      <span className="ml-2 hidden xl:inline">Admin</span>
                      <ChevronDownIcon className="ml-1 h-4 w-4" />
                      
                      {/* Tooltip for smaller screens */}
                      <div className="xl:hidden absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
                        Admin Functions
                      </div>
                    </button>
                    
                    {/* Admin Dropdown Menu */}
                    {isAdminDropdownOpen && (
                      <div className="origin-top-left absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                        <div className="py-1">
                          {adminNavigation.map((item) => {
                            const Icon = item.icon;
                            return (
                              <Link
                                key={item.name}
                                to={item.href}
                                onClick={() => setIsAdminDropdownOpen(false)}
                                className={`${
                                  item.current
                                    ? 'bg-blue-50 text-blue-700'
                                    : 'text-gray-700 hover:bg-gray-100'
                                } group flex items-center px-4 py-2 text-sm transition-colors`}
                              >
                                <Icon className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
                                {item.name}
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Compact Desktop User Menu */}
            <div className="hidden lg:flex lg:items-center flex-shrink-0">
              <div className="relative" ref={dropdownRef}>
                {/* User Avatar - No role badge in top menu */}
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex-shrink-0"
                  title={`${user?.firstName} ${user?.lastName}`}
                >
                  <span className="text-sm font-medium text-white">
                    {user?.firstName?.[0] || 'U'}
                  </span>
                </button>
                
                {/* Compact Desktop Dropdown menu */}
                {isDropdownOpen && (
                  <div className="origin-top-right absolute right-0 mt-2 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                    <div className="py-1">
                      <div className="px-4 py-3 text-sm text-gray-900 border-b border-gray-100">
                        <div className="font-medium text-base">{user?.firstName} {user?.lastName}</div>
                        <div className="text-gray-500 mt-1 text-sm truncate">{user?.email}</div>
                        
                        {/* Role Information */}
                        <div className="mt-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-600">Role:</span>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${roleInfo.bgColor} ${roleInfo.textColor} ${roleInfo.borderColor}`}>
                              {roleInfo.description}
                            </span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center">
                          <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                          Sign out
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile menu button and user avatar */}
            <div className="flex items-center lg:hidden">
              {/* Mobile User Avatar */}
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 mr-2"
              >
                <span className="text-sm font-medium text-white">
                  {user?.firstName?.[0] || 'U'}
                </span>
              </button>
              
              {/* Hamburger Menu Button */}
              <button
                data-testid="mobile-menu-toggle"
                ref={mobileToggleRef}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                aria-expanded={isMobileMenuOpen ? 'true' : 'false'}
              >
                <span className="sr-only">Open main menu</span>
                {isMobileMenuOpen ? (
                  <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <div 
          ref={mobileMenuRef}
          data-testid="mobile-nav-panel"
          className={`${isMobileMenuOpen ? 'block' : 'hidden'} lg:hidden`}
        >
          <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-gray-200">
            {/* Core Navigation Items */}
            {coreNavigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${
                    item.current
                      ? 'bg-blue-50 border-blue-500 text-blue-700'
                      : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                  } block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors duration-200`}
                >
                  <div className="flex items-center">
                    <Icon className="h-5 w-5 mr-3" aria-hidden="true" />
                    {item.name}
                  </div>
                </Link>
              );
            })}


            {/* Admin Navigation Items */}
            {isAdmin && (
              <div className="pt-2 border-t border-gray-200">
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Admin Functions
                </div>
                {adminNavigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`${
                        item.current
                          ? 'bg-blue-50 border-blue-500 text-blue-700'
                          : 'border-transparent text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700'
                      } block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors duration-200`}
                    >
                      <div className="flex items-center">
                        <Icon className="h-5 w-5 mr-3" aria-hidden="true" />
                        {item.name}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
            
            {/* Mobile User Info Section */}
            <div className="border-t border-gray-200 pt-4 pb-3">
              <div className="flex items-center px-4">
                <div className="flex-shrink-0">
                  <UserCircleIcon className="h-10 w-10 text-gray-400" />
                </div>
                <div className="ml-3">
                  <div className="text-base font-medium text-gray-800">
                    {user?.firstName} {user?.lastName}
                  </div>
                  <div className="text-sm font-medium text-gray-500">
                    {user?.email}
                  </div>
                  <div className="mt-1">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${roleInfo.bgColor} ${roleInfo.textColor} ${roleInfo.borderColor}`}>
                      {roleInfo.description}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Mobile Logout Button */}
              <div className="mt-3 px-2">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors duration-200"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" aria-hidden="true" />
                  Sign out
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <main className="flex-1 flex flex-col">
        <div className="max-w-7xl mx-auto w-full flex-1 flex flex-col py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0 flex-1 flex flex-col">
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
                className="h-full"
              >
                {children}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ResponsiveLayout;
